import React, { useState } from 'react';
import { formatMoney } from '../../../utils/money';
import { formatDateCa } from '../legal-board/legalBoardUi';
import { Disclosure, StateCorrection } from '../legal-board/LegalBoardPrimitives';
import { filingAction, downloadIncomePdf, downloadModel182, downloadCertificates } from './api';
import { OfficialLink, DateForm } from './PublicUtilityForms';
import { daysText, missingDonorData, taskTitle } from './calendarCopy';

const euros = (value) => `${formatMoney(value)} €`;

const WHAT = {
    model182: (year) => `La llista de qui ha fet donatius a la colla durant ${year} i quant. Amb ella, l’Agència Tributària posa la desgravació a la renda de cada donant.`,
    accounts: () => 'Cada any, les associacions d’utilitat pública presenten a la Generalitat el balanç, el compte de resultats i la memòria, aprovats per l’assemblea, dins dels 6 mesos després de tancar l’any. Si no es presenten, es pot perdre la declaració.',
    model200: (year) => `L’Impost de Societats de ${year} de les entitats sense ànim de lucre. Hi van tots els ingressos de l’any, encara que quasi tots (quotes, donatius, subvencions) estan exempts. El sol fer el gestor.`,
    economic_report: (year) => `Un resum dels ingressos i les despeses de ${year} i de a què s’han destinat, que demana la Llei 49/2002. Si els ingressos superen 20.000 €, es presenta a l’Agència Tributària; si no, només cal tenir-la feta.`,
};

const MARK = {
    model182: { title: 'Marca’l com a presentat', button: 'Ja l’hem presentat' },
    accounts: { title: 'Marca’ls com a presentats', button: 'Ja els hem presentat' },
    model200: { title: 'Marca’l com a presentat', button: 'Ja l’hem presentat' },
    economic_report: { title: 'Marca-la com a presentada', button: 'Ja l’hem presentada' },
};

/**
 * The screen of one deadline: what it is, until when, and the steps to do it,
 * each with its button, ending with marking it as presented.
 */
function PublicUtilityObligation({ item, overview, userId, canManage, onBack, onOpenDonors, onOpenReport }) {
    const [marking, setMarking] = useState(false);
    const [openStep, setOpenStep] = useState(null);
    const [showWhat, setShowWhat] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [problems, setProblems] = useState([]);
    const { links, fees, colla } = overview;
    const year = item.fiscalYear;
    const totals = overview.donorTotals?.[year] || null;
    const done = item.state === 'done';

    const run = async (action, { back = false } = {}) => {
        if (busy) return;
        setBusy(true);
        setError(null);
        setProblems([]);
        try {
            await action();
            if (back) onBack();
        } catch (actionError) {
            setError(actionError.message || 'No s’ha pogut completar.');
            setProblems(Array.isArray(actionError.payload?.details) ? actionError.payload.details : []);
        } finally {
            setBusy(false);
        }
    };

    const incomeButton = (
        <button type="button" className="legal-board-button secondary small" disabled={busy} onClick={() => run(() => downloadIncomePdf({ userId, year }))}>
            <i className="fas fa-download" aria-hidden="true"></i> Ingressos {year}
        </button>
    );

    const markStep = {
        title: MARK[item.key].title,
        ok: done,
        hint: done ? `Presentat el ${formatDateCa(item.doneOn)}${item.reference ? ` (${item.reference})` : ''}.` : null,
        action: !canManage || done ? null : marking ? (
            <DateForm
                id={`public-utility-mark-${item.key}-${year}`}
                label="Data de presentació"
                withReference
                referenceLabel="Número de justificant (opcional)"
                submitLabel="Desar"
                busy={busy}
                onSubmit={({ date, reference }) => run(() => filingAction({ userId, year, obligation: item.key, action: 'done', date, reference }), { back: true })}
                onCancel={() => setMarking(false)}
            />
        ) : (
            <button type="button" className="legal-board-button secondary small" onClick={() => setMarking(true)} disabled={busy}>{MARK[item.key].button}</button>
        ),
    };

    const STEPS = {
        model182: [
            {
                title: totals?.withoutPostalCode > 0 ? 'Completa les dades que falten' : 'Completa els DNI que falten',
                ok: Boolean(totals) && totals.donors > 0 && totals.withoutNif === 0,
                hint: !totals ? null
                    : totals.donors === 0 ? 'Encara no hi ha cap donatiu aquest any.'
                        : totals.withoutPostalCode > 0 ? `${totals.withoutNif} dels ${totals.donors} donants no hi ${totals.withoutNif === 1 ? 'anirà' : 'aniran'}: ${totals.withoutNif === 1 ? 'falta' : 'falten'} ${missingDonorData(totals)}.`
                        : totals.withoutNif > 0 ? `${totals.withoutNif} dels ${totals.donors} donants no en ${totals.withoutNif === 1 ? 'té i no hi anirà' : 'tenen i no hi aniran'}.`
                            : `Els ${totals.donors} donants tenen DNI.`,
                action: (
                    <button type="button" className={`legal-board-button small ${totals?.withoutNif > 0 ? 'primary' : 'secondary'}`} onClick={() => onOpenDonors(year, totals?.withoutNif > 0 ? 'missing' : 'review')} disabled={busy}>
                        {totals?.withoutNif > 0 && canManage ? (totals.withoutPostalCode > 0 ? 'Completar les dades' : 'Completar els DNI') : 'Veure els donants'}
                    </button>
                ),
            },
            {
                title: 'Descarrega el fitxer del Model 182',
                hint: totals?.declared > 0 ? `${totals.declared} donants · ${euros(totals.declaredAmount)}` : null,
                action: canManage ? (
                    <>
                        <button type="button" className="legal-board-button secondary small" disabled={busy || !totals?.declared} onClick={() => run(() => downloadModel182({ userId, year, nif: colla.nif }))}>
                            <i className="fas fa-download" aria-hidden="true"></i> Descarregar el fitxer
                        </button>
                        {totals?.declared > 0 ? (
                            <p className="legal-board-muted legal-board-small public-utility-step-note">
                                Si algun donant et demana el certificat de donatius,{' '}
                                <button type="button" className="legal-board-inline-action" disabled={busy} onClick={() => run(() => downloadCertificates({ userId, year }))}>descarrega’ls tots en un PDF</button>.
                            </p>
                        ) : null}
                    </>
                ) : null,
            },
            {
                title: 'Puja’l a l’Agència Tributària',
                hint: 'A la seu electrònica, Model 182 › «Presentación mediante fichero», amb el certificat digital de la colla.',
                action: <OfficialLink href={links.model182}>Anar a l’Agència Tributària</OfficialLink>,
            },
            markStep,
        ],
        accounts: [
            {
                title: `Revisa la memòria d’activitats de ${year}`,
                ok: (overview.savedReportYears || []).includes(year),
                hint: 'Aleta hi posa les actuacions, els assajos i els socis; la junta hi afegeix els seus textos.',
                action: <button type="button" className="legal-board-button secondary small" onClick={() => onOpenReport(year)} disabled={busy}>Revisar la memòria</button>,
            },
            {
                title: 'Passa els ingressos al gestor',
                hint: 'Amb això fa el balanç i el compte de resultats, amb el model oficial (el simplificat, en Excel, és a la pàgina del tràmit).',
                action: incomeButton,
            },
            {
                title: 'Que l’assemblea aprovi els comptes',
                hint: 'La secretaria en fa un certificat, signat amb el vistiplau de la presidència.',
            },
            {
                title: 'Presenta-ho a Justícia',
                hint: `Amb el certificat digital de la colla. Hi van el balanç, el compte de resultats, la memòria i el certificat de l’assemblea; la taxa és de ${euros(fees.accounts)}.`,
                action: <OfficialLink href={links.accounts}>Anar al tràmit</OfficialLink>,
            },
            markStep,
        ],
        model200: [
            {
                title: `Passa els comptes de ${year} al gestor`,
                hint: 'L’Impost de Societats es fa amb el compte de resultats de l’any.',
                action: incomeButton,
            },
            {
                title: 'Presenta’l a l’Agència Tributària',
                hint: 'Model 200, amb el certificat digital de la colla.',
                action: <OfficialLink href={links.model200}>Anar a l’Agència Tributària</OfficialLink>,
            },
            markStep,
        ],
        economic_report: [
            {
                title: `Mira si els ingressos de ${year} superen 20.000 €`,
                hint: 'Si no els superen, no cal presentar-la: només tenir-la feta.',
                action: incomeButton,
            },
            {
                title: 'Prepara la memòria econòmica',
                hint: 'Els ingressos i les despeses de l’any i a què s’han destinat. Sovint la fa el gestor amb els comptes.',
            },
            {
                title: 'Presenta-la a l’Agència Tributària',
                action: <OfficialLink href={links.economicReport}>Anar a l’Agència Tributària</OfficialLink>,
            },
            markStep,
        ],
    };

    const steps = STEPS[item.key];
    // Only the step to do is open: the first one not done (none once presented)
    const firstPending = steps.findIndex((step) => !step.ok);
    const currentStep = openStep === null ? (done ? -1 : firstPending) : openStep;

    const when = done
        ? <p className="public-utility-when ok">Presentat el {formatDateCa(item.doneOn)}{item.reference ? ` (${item.reference})` : ''}.</p>
        : item.state === 'overdue'
            ? <p className="public-utility-when late">El termini era el {formatDateCa(item.deadline)}. Si ja ho vau presentar, marca-ho al final.</p>
            : <p className="public-utility-when">Fins al {formatDateCa(item.deadline)} · {daysText(item.daysLeft)}</p>;

    return (
        <section className="legal-board-card public-utility-obligation-screen" aria-labelledby="public-utility-obligation-title">
            <div className="legal-board-wizard-head-actions">
                <button type="button" className="legal-board-button link" onClick={onBack} disabled={busy}>← Tornar</button>
            </div>
            <header className="public-utility-obligation-title">
                <h2 id="public-utility-obligation-title">{taskTitle(item)}</h2>
                {when}
            </header>
            <Disclosure id={`public-utility-what-${item.key}`} summary="Què és" open={showWhat} onToggle={() => setShowWhat((value) => !value)}>
                <p>{WHAT[item.key](year)}</p>
            </Disclosure>
            {error ? (
                <div className="legal-board-alert error" role="alert">
                    {error}
                    {problems.length > 0 ? <ul>{problems.map((problem, index) => <li key={`${problem.code}-${index}`}>{problem.message}</li>)}</ul> : null}
                </div>
            ) : null}
            <ol className="legal-board-flow public-utility-steps" aria-label="Passos">
                {steps.map((step, index) => {
                    const open = index === currentStep;
                    const state = step.ok ? 'done' : open ? 'active' : 'pending';
                    return (
                        <li key={step.title} className={`legal-board-flow-item ${state}`}>
                            <button type="button" className="legal-board-flow-head" aria-expanded={open} aria-controls={`public-utility-step-${index}`} onClick={() => setOpenStep(open ? -1 : index)}>
                                <span className={`legal-board-flow-icon ${state}`} aria-hidden="true">{step.ok ? <i className="fas fa-check"></i> : index + 1}</span>
                                <span className="legal-board-flow-title">{step.ok ? <span className="legal-board-sr-only">Fet. </span> : null}{step.title}</span>
                                <span className="legal-board-flow-summary"></span>
                                <i className={`fas fa-chevron-${open ? 'up' : 'down'} legal-board-flow-chevron`} aria-hidden="true"></i>
                            </button>
                            {open ? (
                                <div id={`public-utility-step-${index}`} className="legal-board-flow-body">
                                    {step.hint ? <p className="public-utility-step-hint">{step.hint}</p> : null}
                                    {step.action ? <div className="public-utility-step-action">{step.action}</div> : null}
                                </div>
                            ) : null}
                        </li>
                    );
                })}
            </ol>
            {done && canManage ? (
                <StateCorrection
                    id={`public-utility-unmark-${item.key}-${year}`}
                    label="Desmarcar com a presentat"
                    description="Tornarà a constar com a pendent a Aleta. No retira res de l’administració."
                    onClick={() => run(() => filingAction({ userId, year, obligation: item.key, action: 'revert' }), { back: true })}
                    disabled={busy}
                />
            ) : null}
        </section>
    );
}

export default PublicUtilityObligation;
